// *************** Angular Imports ***************
import { AfterViewInit, Component } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';

// *************** Angular Material Imports ***************
import { MatIconRegistry } from '@angular/material/icon';

// *************** Third-Party Library Imports ***************
import { skip, take } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit{
  // *************** PRIVATE VARIABLES ***************
  private appId: null | string = '';
  private roomName: null | string = '';
  private jwt: null | string = '';
  private baseApi: string = 'frankfurt.8x8.vc';

  /**
   * Represents the main component of the application.
   */
  constructor(
    private _matIconRegistry: MatIconRegistry,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  // *************** LIFECYCLE HOOKS ***************
  /**
   * Lifecycle hook that runs after the component's view has been fully initialized.
   * 
   * - Clears local storage to reset any previous state.
   * - Sets the default font set for Angular Material icons.
   * - Subscribes to query parameters in the URL (after the first emission)
   *   to retrieve values for `appId`, `roomName`, and `jwt`.
   * - Initializes the JAAS video conferencing setup using the retrieved values.
   */
  ngAfterViewInit(): void {
    localStorage.clear();
    this._matIconRegistry.setDefaultFontSetClass('material-icons-round');
    this.route.queryParamMap.pipe(skip(1), take(1)).subscribe(async (queryParamMap) => {
      this.appId = queryParamMap.get('a');
      const roomName = queryParamMap.get('r');
      this.roomName = roomName?.replace(/^production/, 'jury') ?? null;
      this.jwt = queryParamMap.get('j');

      if (roomName !== this.roomName) {
        await this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { r: this.roomName },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }

      this.initJAAS();
    });
  }

  // *************** INIT / LOAD DATA ***************
  /**
   * Initializes the JAAS (Jitsi as A Service) by dynamically adding a script tag to the HTML head.
   * The script source is set to the external_api.js file hosted on the frankfurt.8x8.vc server.
   * This method requires the `appId`, `roomName`, and `jwt` properties to be set.
   */
  initJAAS() {
    if (this.appId && this.roomName && this.jwt) {
      let node = document.createElement('script');
      node.src = `https://${this.baseApi}/${this.appId}/external_api.js`;
      node.type = 'text/javascript';
      node.async = true;
      node.onload = this.afterScriptLoaded.bind(this)
      node.charset = 'utf-8';
      document.getElementsByTagName('head')[0].appendChild(node);
    }
  }

  /**
   * Callback function that is executed after the Jitsi Meet script has been loaded.
   * Initializes the Jitsi Meet External API with the specified options.
   */
  afterScriptLoaded() {
    const JitsiMeetExternalAPI = (<any>window).JitsiMeetExternalAPI
    const options = {
      roomName: `${this.appId}/${this.roomName}`,
      parentNode: document.querySelector('#jaas-container'),
      lang: 'fr',
      jwt: this.jwt,

      configOverwrite: {
        transcription: {
          enabled: false,
          autoTranscribeOnRecord: false,
          disableClosedCaptions: true,
        },
      },
    };

    new JitsiMeetExternalAPI(this.baseApi, options);
  }
}
